/**
 * Fold a conversation's workflow-run messages into per-run activity groups.
 *
 * The backend stamps every persisted workflow message with a marker the
 * history fetch passes through verbatim (`metadata.references[]` with
 * `ref_kind: "workflow_run"`):
 *   - `kind: "milestone"`  — an intermediate message (brief questions, QA
 *     reply, review notice). Folded INTO the run's accordion as a step.
 *   - `kind: "final"`      — the run's published summary. Stays in the list
 *     as the run's one regular bubble; its `ref_body.steps` carry the
 *     engine's progress trail (titles + timestamps) for rehydration.
 *
 * Given the raw message list (history rows and/or live rows, including any
 * live `activity_group` pseudo-messages built from streaming frames), this
 * returns the display list where:
 *   - milestone messages are removed from the flow,
 *   - one `activity_group` row per run sits where the run happened
 *     (at the first folded element's position), holding progress steps and
 *     milestone steps merged in time order,
 *   - the final message follows as a normal bubble.
 *
 * Pure and defensive: messages without markers pass through untouched, and a
 * malformed marker never throws.
 */

const MARKER_KIND = 'workflow_run';

/** A step row's payload, tolerating both raw and TVF (payload-nested) shapes. */
const stepPayload = (s) => (s && typeof s === 'object' ? (s.payload ?? s) : {});

/**
 * Extract the workflow_run marker from a message, or null.
 *
 * Primary carrier: a marker STEP in the message's persisted plan steps
 * (`step_id: "workflow_run"`, `title`: milestone|final, `goal_text`: the
 * run's response_id) — steps are the one processing field the message-list
 * endpoint verifiably returns per message. `metadata.references` is kept as
 * a fallback for transports that pass metadata through.
 */
export const workflowRunMarker = (message) => {
  const steps = message?.steps;
  if (Array.isArray(steps)) {
    for (const s of steps) {
      const p = stepPayload(s);
      if (p.step_id === MARKER_KIND && p.goal_text) {
        return { response_id: p.goal_text, kind: p.title };
      }
    }
  }
  const refs = message?.metadata?.references;
  if (!Array.isArray(refs)) return null;
  for (const ref of refs) {
    const kind = ref?.ref_kind ?? ref?.refKind;
    if (kind !== MARKER_KIND) continue;
    const body = ref?.ref_body ?? ref?.refBody;
    if (body && typeof body === 'object' && body.response_id) return body;
  }
  return null;
};

const toMs = (value) => {
  const t = value ? Date.parse(value) : NaN;
  return Number.isNaN(t) ? 0 : t;
};

/** Steps recorded by the engine on the final message → accordion steps.
 *  Tolerates raw shape ({step_id,title,started_at}) and the TVF shape
 *  ({payload:{step_id,title}, startedAt}); the marker step is excluded. */
const progressSteps = (finalMessage, marker) => {
  const source = Array.isArray(finalMessage?.steps)
    ? finalMessage.steps
    : Array.isArray(marker?.steps)
      ? marker.steps
      : [];
  return source
    .map((s, i) => {
      const p = stepPayload(s);
      return {
        id: p.step_id ?? `progress:${i}`,
        text: p.title ?? '',
        kind: 'progress',
        startedAt: s.started_at ?? s.startedAt ?? null,
      };
    })
    .filter((s) => s.id !== MARKER_KIND && s.text);
};

export const foldWorkflowRuns = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) return messages;

  // First pass: find each run's folded pieces and where the run starts.
  const runs = new Map(); // responseId -> { milestones: [], finalMarker, firstIndex }
  let sawMarker = false;
  messages.forEach((m, index) => {
    const marker = workflowRunMarker(m);
    if (!marker) return;
    sawMarker = true;
    const run = runs.get(marker.response_id) ?? {
      milestones: [],
      finalMarker: null,
      finalMessage: null,
      firstIndex: index,
    };
    if (marker.kind === 'milestone') {
      run.milestones.push({
        // The persisted message uuid is the canonical step id (live groups
        // dedupe refetched milestones against it); the positional fallback
        // keeps the id stable when a row carries neither uuid nor id.
        id: m.messageUuid ?? m.id ?? `milestone:${marker.response_id}:${index}`,
        text: m.content ?? '',
        kind: 'milestone',
        startedAt: m.createdAt ?? m.timestamp ?? null,
      });
    } else if (marker.kind === 'final') {
      run.finalMarker = marker;
      run.finalMessage = m;
    }
    runs.set(marker.response_id, run);
  });
  if (!sawMarker) return messages;

  // A live streaming group already covers its run — prefer it and only fold
  // the milestone rows away (their text is already a step in the live group,
  // keyed by the same message uuid, or arrives with the next frame).
  const liveGroupIds = new Set(
    messages
      .filter((m) => m.role === 'activity_group' && m.responseId)
      .map((m) => m.responseId)
  );

  const out = [];
  const emitted = new Set();
  messages.forEach((m, index) => {
    if (m.role === 'activity_group' && m.responseId) {
      // Live streaming group: enrich it with any folded milestones it does
      // not already hold (e.g. rows that came back in a mid-run history
      // refetch after the live steps were wiped). Step ids are the persisted
      // message uuids on both sides, so the dedupe is exact.
      const run = runs.get(m.responseId);
      if (run?.milestones.length) {
        const have = new Set((m.steps ?? []).map((s) => s.id));
        const missing = run.milestones.filter((s) => !have.has(s.id));
        out.push(
          missing.length ? { ...m, steps: [...missing, ...(m.steps ?? [])] } : m
        );
      } else {
        out.push(m);
      }
      emitted.add(m.responseId);
      return;
    }
    const marker = workflowRunMarker(m);
    if (marker?.kind === 'milestone') {
      // Folded into the group; emit the group at the run's first position.
      maybeEmitGroup(marker.response_id, index);
      return;
    }
    if (marker?.kind === 'final') {
      maybeEmitGroup(marker.response_id, index);
      out.push(m);
      return;
    }
    out.push(m);
  });
  return out;

  function maybeEmitGroup(responseId, index) {
    if (emitted.has(responseId)) return;
    if (liveGroupIds.has(responseId)) {
      // A live streaming group already covers this run.
      emitted.add(responseId);
      return;
    }
    const run = runs.get(responseId);
    // Emit exactly once, at the run's first folded element.
    if (!run || run.firstIndex !== index) return;
    emitted.add(responseId);
    const steps = [
      ...(run.finalMarker
        ? progressSteps(run.finalMessage, run.finalMarker)
        : []),
      ...run.milestones,
    ].sort((a, b) => toMs(a.startedAt) - toMs(b.startedAt));
    if (steps.length === 0) return;
    out.push({
      id: `activity:${responseId}`,
      role: 'activity_group',
      responseId,
      steps,
      activityStatus: run.finalMarker ? 'done' : 'running',
      createdAt: steps[0].startedAt ?? null,
      timestamp: steps[0].startedAt ?? null,
    });
  }
};

export default foldWorkflowRuns;
